import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { KeyRound, Plus, UserPlus } from "lucide-react";
import type { ActionAvailability } from "@amber/contracts";
import { AppLink, Button, ReadOnlyNote, SectionHeader, TabList, TabPanel, formatDateTime } from "@amber/ui";
import { ACCESS_ACTIONS, type SessionResponse } from "../contracts/index";
import { credentialsQuery, domainsQuery, membersQuery } from "./queries";
import type { UsersSearch } from "./search";
import { find, reasonOf } from "./actions";
import { AccessDialogs, type DialogState } from "./dialogs/AccessDialogs";
import { UsersTable } from "./users/UsersTable";
import { DomainsTable } from "./DomainsTable";
import { CredentialsTable } from "./CredentialsTable";

function TabAction({
  tab,
  invite,
  addDomain,
  issue,
  onDialog,
}: {
  tab: NonNullable<UsersSearch["tab"]>;
  invite: ActionAvailability | undefined;
  addDomain: ActionAvailability | undefined;
  issue: ActionAvailability | undefined;
  onDialog: (state: DialogState) => void;
}) {
  if (tab === "users")
    return (
      <Button
        variant="primary"
        disabledReason={reasonOf(invite)}
        onClick={() => {
          onDialog({ kind: "invite" });
        }}
      >
        <UserPlus aria-hidden="true" />
        Invite…
      </Button>
    );
  if (tab === "domains")
    return (
      <Button
        variant="primary"
        disabledReason={reasonOf(addDomain)}
        onClick={() => {
          onDialog({ kind: "addDomain" });
        }}
      >
        <Plus aria-hidden="true" />
        Add domain…
      </Button>
    );
  return (
    <Button
      variant="primary"
      disabledReason={reasonOf(issue)}
      onClick={() => {
        onDialog({ kind: "issueCredential" });
      }}
    >
      <KeyRound aria-hidden="true" />
      Issue…
    </Button>
  );
}

export function UsersAndRoles({
  session,
  search,
  onSearch,
}: {
  session: SessionResponse;
  search: UsersSearch;
  onSearch: (next: UsersSearch) => void;
}) {
  const members = useQuery(membersQuery);
  const domains = useQuery(domainsQuery);
  const credentials = useQuery(credentialsQuery);
  const [dialog, setDialog] = useState<DialogState>(null);
  const tab = search.tab ?? "users";
  const invite = members.data ? find(members.data.actions, ACCESS_ACTIONS.invite) : undefined;
  const addDomain = domains.data ? find(domains.data.actions, ACCESS_ACTIONS.addDomain) : undefined;
  const issue = credentials.data ? find(credentials.data.actions, ACCESS_ACTIONS.issueCredential) : undefined;
  const close = () => {
    setDialog(null);
  };

  return (
    <section aria-labelledby="section-title" className="flex min-h-0 flex-1 flex-col">
      <SectionHeader
        titleId="section-title"
        title="Users and roles"
        lead="Who can use the workspace and what they can do. Everyone signs in with Google Workspace. Each person has one role, assigned here; roles are cumulative, so an Operator can do everything an Editor can."
        meta={
          members.data ? (
            <>
              {members.data.last_changed ? (
                <span>
                  Last changed by <b>{members.data.last_changed.by}</b> · {formatDateTime(members.data.last_changed.at)}
                </span>
              ) : (
                <span>Not changed yet</span>
              )}
              <AppLink href="/settings/audit?action=access" className="font-strong text-link">
                Audit log ({members.data.audit_count})
              </AppLink>
              {invite && !invite.enabled ? (
                <ReadOnlyNote>Read-only for your role · Admins can change this</ReadOnlyNote>
              ) : null}
            </>
          ) : null
        }
        actions={<TabAction tab={tab} invite={invite} addDomain={addDomain} issue={issue} onDialog={setDialog} />}
      />
      <TabList
        label="Users and roles"
        value={tab}
        items={[
          { value: "users", label: "Users", ...(members.data ? { count: members.data.counts.all } : {}) },
          {
            value: "domains",
            label: "Auto-join domains",
            ...(domains.data ? { count: domains.data.rows.length } : {}),
          },
          {
            value: "credentials",
            label: "Machine credentials",
            ...(credentials.data ? { count: credentials.data.rows.filter((r) => r.state === "active").length } : {}),
          },
        ]}
        onChange={(next) => {
          onSearch({ tab: next === "users" ? undefined : next });
        }}
      >
        <TabPanel value="users">
          <UsersTable session={session} search={search} onSearch={onSearch} onDialog={setDialog} />
        </TabPanel>
        <TabPanel value="domains">
          <DomainsTable onDialog={setDialog} />
        </TabPanel>
        <TabPanel value="credentials">
          <CredentialsTable onDialog={setDialog} />
        </TabPanel>
      </TabList>

      <AccessDialogs dialog={dialog} onClose={close} />
    </section>
  );
}
